import { Component, OnInit, Input, ViewChild, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UtilsService } from 'src/app/common';
import { UIChart } from 'primeng/chart';
import { ChartModule } from 'primeng/chart';
import { CardModule } from 'primeng/card';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'gig-position-distribution-chart',
  standalone: true,
  imports: [CommonModule, ChartModule, CardModule, TranslateModule, TooltipModule],
  templateUrl: './position-distribution-chart.component.html',
  styleUrls: ['./position-distribution-chart.component.scss']
})
export class PositionDistributionChartComponent implements OnInit, OnChanges {

  ngOnChanges(changes: SimpleChanges): void {
    this.setupData();
  }


  public lineChartOptions: any = {
    responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 2,
    plugins: {
      legend: {
        labels: {
          usePointStyle: true
            },
            onHover: (event, legendItem) => {
                const canvas = event.native.target;
                canvas.style.cursor = 'pointer';
              }
      }
    }
  };

  public barChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          usePointStyle: true
        },
        onHover: (event, legendItem) => {
            const canvas = event.native.target;
            canvas.style.cursor = 'pointer';
          }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grace: '2%',
      }
    }
  }

  public doughtnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'bottom'
        }
    },
  }


  public horizontalBarChartOptions = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    aspectRatio: 0.8,
    responsive: true,
    layout: {
      padding: {
          top: 10
      }
  },
    plugins: {
      legend: {
        labels: {
          usePointStyle: true
            },
            onHover: (event, legendItem) => {
                const canvas = event.native.target;
                canvas.style.cursor = 'pointer';
              }
      }
    },
    scales: {
        x: {
            beginAtZero: true,
            grace: '2%',
            ticks: {
                font: {
                    weight: 500
                }
            },
            grid: {
                drawBorder: false
            }
        },
        y: {
            ticks: {
                width: '10px'
            },
            grid: {
                drawBorder: false
            }
        }
    }
  }

  public stackedBarChartOptions = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    aspectRatio: 0.8,
    responsive: true,
    layout: {
      padding: {
          top: 10
      }
  },
    plugins: {
      legend: {
        labels: {
          usePointStyle: true
            },
            onHover: (event, legendItem) => {
                const canvas = event.native.target;
                canvas.style.cursor = 'pointer';
              }
      }
    },
    scales: {
        x: {
            beginAtZero: true,
            grace: '2%',
            ticks: {
                font: {
                    weight: 500
                }
            },
            grid: {
                drawBorder: false
            }
        },
        y: {
            ticks: {
                width: '10px'
            },
            grid: {
                drawBorder: false
            }
        }
    }
  }

  private defaultColors = [
    { hex: '#873EFE', rgba: 'rgba(135, 62, 254, 1)' },
    { hex: '#44486d', rgba: 'rgba(68, 72, 109, 1)' },
    { hex: '#FFF73D', rgba: 'rgba(255, 247, 61, 1)' },
    { hex: '#5E8065', rgba: 'rgba(94, 128, 101, 1)' },
    { hex: '#3D70FF', rgba: 'rgba(61, 112, 255, 1)' },
    { hex: '#FFA73D', rgba: 'rgba(252, 167, 61, 1)' },
    { hex: '#EC4DBC', rgba: 'rgba(236, 77, 188, 1)' },
    { hex: '#FFA900', rgba: 'rgba(255, 169, 0, 1)' },
    { hex: '#0DC7A3', rgba: 'rgba(13, 199, 163, 1)' },
    { hex: '#9B51E0', rgba: 'rgba(155, 81, 224, 1)' },
    { hex: '#FF407B', rgba: 'rgba(255, 64, 123, 1)' },
    { hex: '#7CDDCF', rgba: 'rgba(124, 221, 207, 1)' }
  ];

  public data: any;
  public options: any;

  private colors: any;

  @ViewChild('chart') chart: UIChart;

  @Input() chartType: string;
  @Input() chartPositionNames: {};
  @Input() chartColors: any[];
  @Input() chartData: any;
  @Input() parameters: string[];
  @Input() type: string;
  @Input() height: string = '20rem';
  @Input() stacked: boolean = false;
  @Input() toolTipPath: string;
  @Input() tipLocation: string = 'top';
  @Input() dateFormatter: string = 'MMM YYYY';

  constructor(private utils: UtilsService, private translate: TranslateService) { }


  ngOnInit(): void {
    this.setupData();
  }

  public refresh() {
    this.setupData();
  }

  public configureChart(data: any, key: string = '', type: string = ''): any {

    if (!data || !data.length) {
      return null;
    }

    let chartData = {
      labels: [],
      datasets: []
    };

    let dataCopy = null;

    if (!type) {
      dataCopy = this.sortItemsByDateAscending(data);
    } else
    {
      dataCopy = this.sortItemsByMonthYearAscending(data);
    }


    dataCopy.forEach(item => {

      if (this.type === 'default') {
        let date = item.date.replace(' ', 'T').replace(' +', '+');
        const label = this.utils.formatDate(date, this.dateFormatter);
        chartData.labels.push(label);

        Object.keys(item).forEach(prop => {
          const datasetNames = Object.keys(this.chartPositionNames);

          if (datasetNames.includes(prop)) {
            this.configureBacklinksDataset(chartData, item, prop);
          } else if (this.type === 'estimated_traffic' && prop.startsWith('etv')) {
            let dataset = chartData.datasets.find(d => d.id === prop);

            const color = this.colors?.shift();
            if (!dataset) {
              dataset = {
                label: 'Estimated Traffic Value',
                data: [],
                fill: true,
                borderColor: color?.hex,
                pointBackgroundColor: color?.hex,
                backgroundColor: color?.rgba,
                tension: 0,
                id: prop
              };
              chartData.datasets.push(dataset);
            }
            dataset.data.push(item[key][type][prop]);
          }
      });


      } else {
        const label = `${item.year}-${item.month}-01`;
        chartData.labels.push(this.utils.formatDate(label, this.dateFormatter));

        if (item[key][type] === null) {
          return;
        }
        Object.keys(item[key][type]).forEach(prop => {
            if (this.type === 'position_distribution' && prop.startsWith('pos_')) {
            let dataset = chartData.datasets.find(d => d.id === prop);

            const color = this.colors?.shift();
            if (!dataset) {
              dataset = {
                label: this.chartPositionNames[prop],
                data: [],
                fill: false,
                borderColor: color?.hex,
                pointBackgroundColor: color?.hex,
                backgroundColor: color?.rgba,
                tension: 0,
                id: prop
              };
              chartData.datasets.push(dataset);
            }
            dataset.data.push(item[key][type][prop]);
          } else if (this.type === 'estimated_traffic' && prop.startsWith('etv')) {
            let dataset = chartData.datasets.find(d => d.id === prop);

            const color = this.colors?.shift();
            if (!dataset) {
              dataset = {
                label: 'Estimated Traffic Value',
                data: [],
                fill: false,
                borderColor: color?.hex,
                pointBackgroundColor: color?.hex,
                backgroundColor: color?.rgba,
                tension: 0,
                id: prop
              };
              chartData.datasets.push(dataset);
            }
            dataset.data.push(item[key][type][prop]);
          }
        });
      }

    });
    return chartData;

  }

  private combinePositions(data, key: string, type: string) {
    for (let i = 0; i < data.length; i++) {
      const position: any = {};
      const organic = JSON.parse(JSON.stringify(data[i][key][type]));
      const top3 = organic?.pos_1 + organic?.pos_2_3;
      const pos21_50 = organic?.pos_21_30 + organic?.pos_31_40 + organic?.pos_41_50;
      const pos51_100 = organic?.pos_51_60 + organic?.pos_61_70 + organic?.pos_71_80 + organic?.pos_81_90 + organic?.pos_91_100;
      position.pos_3 = top3;
      position.pos_4_10 = organic?.pos_4_10;
      position.pos_11_20 = organic?.pos_11_20;
      position.pos_21_50 = pos21_50;
      position.pos_51_100 = pos51_100;
      delete organic?.pos_1;
      delete organic?.pos_2_3;
      delete organic?.pos_21_30;
      delete organic?.pos_31_40;
      delete organic?.pos_41_50;
      delete organic?.pos_51_60;
      delete organic?.pos_61_70;
      delete organic?.pos_71_80;
      delete organic?.pos_81_90;
      delete organic?.pos_91_100;

      if (type === 'organic') {
        data[i].metrics.organic = position;
      } else if (type === 'paid') {
        data[i].metrics.paid = position;
      }

    }
    return data;
  }

  private configureBacklinksDataset(chartData, item, prop): any {
    let dataset = chartData.datasets.find(d => d.id === prop);

    const color = this.colors?.shift();
    if (!dataset) {
      dataset = {
        label: this.chartPositionNames[prop],
        data: [],
        fill: false,
        borderColor: color?.hex,
        pointBackgroundColor: color?.hex,
        backgroundColor: color?.rgba,
        tension: 0,
        id: prop
      };
      chartData.datasets.push(dataset);
    }
    dataset.data.push(item[prop]);
  }

  public getYearlyChartData(searches): any {
    if (!searches) {
      return {};
    }

    this.utils.logger.info(`KeywordSearchService.getYearlyChartData() - searches.`);
    const data = this.getYearlySearchVol(searches);
    const color = this.colors?.shift();
    return {
      labels: data?.labels,
      datasets: [
        {
          label: this.translate.instant('keywordSearchService.yearlyChardData.label'),
          tension: 0,
          data: data?.searchVolume,
          fill: false,
          borderColor: color?.hex,
          pointBackgroundColor: color?.hex,
          backgroundColor: color?.rgba,
        }
      ]
    }
  }

    private getWeekSessionsLineChart(data: any[]): any {
    if (!data) return {};
    return {
        labels: data.map(e => `Week ${e.week}`),
        datasets: [
        {
            label: 'Sessions',
            data: data.map(e => e.sessions),
            fill: false,
            borderColor: '#42A5F5',
            pointBackgroundColor: '#42A5F5',
            backgroundColor: '#42A5F5',
            tension: 0.4
        }
        ]
    };
    }

  private getYearlySearchVol(data: any[]): any {
    if (!data) {
      throw new Error('No data provided');
    }

    this.utils.logger.info(`KeywordSearchService.getYearlySearchVol() - data.`);

    const dataSet = {
      searchVolume: [],
      labels: []
    }


    data.forEach(e => {
      dataSet.searchVolume.unshift(e?.searchVolume)
      dataSet.labels.unshift(this.utils.formatDate(`${e?.year}-${e?.month}-${'01'}`, this.dateFormatter));
    });

    return dataSet;
  }

  private getHistoricalVisibilityChanges(data: any[]): any {

    const dataSet = {
      visibility: [],
      labels: []
    }

    data.forEach((item: any) => {
      dataSet.visibility.unshift(this.getOrganicRankingCoverageCount(item));
      dataSet.labels.unshift(this.utils.formatDate(`${item?.year}-${item?.month}-${'01'}`, this.dateFormatter));
    });
    const color = this.colors?.shift();
    return {
      labels: dataSet?.labels,
      datasets: [
        {
          label: 'Historical Visibility Changes',
          tension: 0,
          data: dataSet?.visibility,
          fill: false,
          borderColor: color?.hex,
          pointBackgroundColor: color?.hex,
          backgroundColor: color?.rgba,
        }
      ]
    }
  }

  private getHistoricalRankAverage(data: any[]): any {

    const dataSet = {
      averageRank: [],
      labels: []
    }

    data.forEach((item: any) => {
      dataSet.averageRank.unshift(this.getOrganicAverageRank(item));
      dataSet.labels.unshift(this.utils.formatDate(`${item?.year}-${item?.month}-${'01'}`, this.dateFormatter));
    });
    const color = this.colors?.shift();
    return {
      labels: dataSet?.labels,
      datasets: [
        {
          label: 'Historical Avg. Ranking Changes',
          tension: 0,
          data: dataSet?.averageRank,
          fill: false,
          borderColor: color?.hex,
          pointBackgroundColor: color?.hex,
          backgroundColor: color?.rgba,
        }
      ]
    }
  }

  private getNewKeywordsOverTime(data: any[]): any {

    const dataSet = {
      traffic: [],
      labels: []
    }

    data.forEach((item: any) => {
      dataSet.traffic.unshift(`${item?.metrics?.organic?.isNew}`);
      dataSet.labels.unshift(this.utils.formatDate(`${item?.year}-${item?.month}-${'01'}`, this.dateFormatter));
    });
    const color = this.colors?.shift();
    return {
      labels: dataSet.labels,
      datasets: [
        {
          label: 'New Keywords Over Time',
          tension: 0,
          data: dataSet?.traffic,
          fill: false,
          borderColor: color?.hex,
          pointBackgroundColor: color?.hex,
          backgroundColor: color?.rgba
        }
      ]
    }
  }

  private getLostKeywordsOverTime(data: any[]): any {

    const dataSet = {
      traffic: [],
      labels: []
    }

    data.forEach((item: any) => {
      dataSet.traffic.unshift(`${item?.metrics?.organic?.isLost}`);
      dataSet.labels.unshift(this.utils.formatDate(`${item?.year}-${item?.month}-${'01'}`, this.dateFormatter));
    });
    const color = this.colors?.shift();
    return {
      labels: dataSet?.labels,
      datasets: [
        {
          label: 'Lost Keywords Over Time',
          tension: 0,
          data: dataSet?.traffic,
          fill: false,
          borderColor: color?.hex,
          pointBackgroundColor: color?.hex,
          backgroundColor: color?.rgba,
        }
      ]
    }
  }

  private getRelevantPages(data: any[]): any {
    const dataSet = {
      values: {
        top3:[],
        top10: []
      },
      labels: []
    }

    data.forEach((item: any) => {
      dataSet.values.top3.unshift(`${item?.metrics?.organic?.pos_1 + item?.metrics?.organic?.pos_2_3}`);
      dataSet.values.top10.unshift(`${item?.metrics?.organic?.pos_1 + item?.metrics?.organic?.pos_2_3 + item?.metrics?.organic?.pos_4_10}`);
      dataSet.labels.unshift(this.splitUrlIntoParts(item?.pageAddress));
    });
    const color = this.colors?.shift();
    const color1 = this.colors?.shift();
    return {
      labels: dataSet.labels,
      datasets: [
        {
          label: 'Keywords in top 3',
          tension: 0,
          data: dataSet?.values?.top3,
          fill: false,
          borderColor: color?.hex,
          pointBackgroundColor: color?.hex,
          backgroundColor: color?.rgba,
          barThickness: 20
        },
        {
          label: 'Keywords in top 10',
          tension: 0,
          data: dataSet?.values?.top10,
          fill: false,
          borderColor: color1?.hex,
          pointBackgroundColor: color1?.hex,
          backgroundColor: color1?.rgba,
          barThickness: 20
        }
      ]
    }
  }

  private getTop3Campaigns(data: any[]) : any {
     if(!data ) return {};

     const color = this.colors?.shift() || { hex: '#42A5F5', rgba: 'rgba(66,165,245,1)' };

     return {
        labels: data.map(e => e.label),
        datasets:  [
            {
                label: 'Conversions',
                data: data.map(e => e.value),
                backgroundColor: color.hex,
                borderColor: color.hex,
                barThickness: 20
            }
        ]
     }
  }

  private doughnutChart(data: any[]): any {
    if(!data) return {};

    const colors = (this.chartColors && this.chartColors.length >= data.length)
        ? this.chartColors.map(c => c.hex)
        : this.defaultColors.slice(0, data.length).map(c => c.hex);

    return {
        labels: data.map(e => e.label),
        datasets: [
            {
                label: 'Traffic Source',
                data: data.map(e => e.value),
                backgroundColor: colors,
                borderColor: colors,
                hoverOffset: 4
            }
        ]
    }
}

  private getVisitByUserType(data: any[]): any {
    if(!data ) return {};

    this.utils.logger.info(`GoogleAnalyticsKpiService.getVisitByUserType() - data.`, data);

     const color = this.colors?.shift() || { hex: '#42A5F5', rgba: 'rgba(66,165,245,1)' };

     return {
        labels: data.map(e => e.userType),
        datasets:  [
            {
                label: 'User By Type',
                data: data.map(e => e.sessions),
                backgroundColor: color.hex,
                borderColor: color.hex,
                barThickness: 60

            }
        ]
     }
  }

  private splitUrlIntoParts(url) {
    const parsedUrl = new URL(url);
    let domain = parsedUrl?.hostname;

    // Remove subdomains, keep only second-level domain and top-level domain
    const domainParts = domain.split('.');
    if (domainParts.length > 2) {
        domain = domainParts.slice(domainParts.length - 2).join('.');
    }

    // Split and filter path segments
    const pathSegments = parsedUrl.pathname.split('/').filter(segment => segment.trim() !== '');

    // Prepend a slash to each path segment
    const formattedSegments = pathSegments.map(segment => `/${segment}`);

    // Combine domain and path segments
    return [domain, ...formattedSegments];
  }

  private getOrganicRankingCoverageCount(item: any): number {
    const organicMetrics = item?.metrics?.organic;
    if (!organicMetrics) {
      return 0;
    }

    if (typeof organicMetrics.count === 'number') {
      return organicMetrics.count;
    }

    return this.getOrganicPositionBuckets().reduce((total: number, bucket: { key: string }) => {
      return total + (Number(organicMetrics?.[bucket.key] ?? 0) || 0);
    }, 0);
  }

  private getOrganicAverageRank(item: any): number | null {
    const organicMetrics = item?.metrics?.organic;
    if (!organicMetrics) {
      return null;
    }

    const buckets = this.getOrganicPositionBuckets();
    const totalKeywords = buckets.reduce((total: number, bucket: { key: string; midpoint: number }) => {
      return total + (Number(organicMetrics?.[bucket.key] ?? 0) || 0);
    }, 0);

    if (totalKeywords === 0) {
      return null;
    }

    const weightedRankTotal = buckets.reduce((total: number, bucket: { key: string; midpoint: number }) => {
      const bucketCount = Number(organicMetrics?.[bucket.key] ?? 0) || 0;
      return total + (bucketCount * bucket.midpoint);
    }, 0);

    return Number((weightedRankTotal / totalKeywords).toFixed(1));
  }

  private getOrganicPositionBuckets(): Array<{ key: string; midpoint: number }> {
    return [
      { key: 'pos_1', midpoint: 1 },
      { key: 'pos_2_3', midpoint: 2.5 },
      { key: 'pos_4_10', midpoint: 7 },
      { key: 'pos_11_20', midpoint: 15.5 },
      { key: 'pos_21_30', midpoint: 25.5 },
      { key: 'pos_31_40', midpoint: 35.5 },
      { key: 'pos_41_50', midpoint: 45.5 },
      { key: 'pos_51_60', midpoint: 55.5 },
      { key: 'pos_61_70', midpoint: 65.5 },
      { key: 'pos_71_80', midpoint: 75.5 },
      { key: 'pos_81_90', midpoint: 85.5 },
      { key: 'pos_91_100', midpoint: 95.5 }
    ];
  }


  private setupData(): void {
    if (!this.chartData || !this.chartData.length) {
      return null;
    }

    if (this.chartType === 'bar') {
      if (this.stacked) {
        this.options = this.stackedBarChartOptions;
      } else {
        this.options = this.type === 'top_relevant_pages' ? this.horizontalBarChartOptions : this.type === 'top3_campaigns' ? this.horizontalBarChartOptions : this.barChartOptions;
      }
    } else {
      this.options = this.lineChartOptions;
    }

    const data = JSON.parse(JSON.stringify(this.chartData));
    this.colors = this.chartColors ? JSON.parse(JSON.stringify(this.chartColors)) : JSON.parse(JSON.stringify(this.defaultColors));

    if (this.type === 'default') {
      this.data = this.configureChart(data);
    } else if (this.type === 'traffic_source') {
        this.data = this.doughnutChart(data);
        this.options = this.doughtnutChartOptions;
    }else if( this.type === 'visit_by_user_type'){
      this.data = this.getVisitByUserType(data);
    } else if (this.type === 'search_volume'){
      this.data = this.getYearlyChartData(data);
    } else if (this.type === 'historical_visibility_changes') {
      this.data = this.getHistoricalVisibilityChanges(data);
    } else if (this.type === 'historical_rank_average') {
      this.data = this.getHistoricalRankAverage(data);
    } else if (this.type === 'new_keywords_over_time') {
      this.data = this.getNewKeywordsOverTime(data);
    } else if (this.type === 'lost_keywords_over_time') {
      this.data = this.getLostKeywordsOverTime(data);
    } else if (this.type === 'top_relevant_pages') {
      this.data = this.getRelevantPages(data);
    } else if (this.type === 'weekly_sessions') {
        this.data = this.getWeekSessionsLineChart(data);
    } else if(this.type == 'top3_campaigns') {
        this.data = this.getTop3Campaigns(data);
    } else {
      if (this.type === 'position_distribution') {
        this.combinePositions(data, this.parameters[0], this.parameters[1]);
      }

      if (this.parameters) {
        this.data = this.configureChart(data, this.parameters[0], this.parameters[1]);
      }

    }

  }

  private sortItemsByMonthYearAscending(items) {
    items.sort((a, b) => {
        if (b.year !== a.year) {
            return a.year - b.year;
        } else {
            return a.month - b.month;
        }
    });
    return items;
  }

  private sortItemsByDateAscending(items) {
    items.sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
    return items;
  }

}
